import { compareObjects } from './compare';
import { matchPattern } from './matcher-cache';

/**
 * Event-trigger matching (EID-64).
 *
 * A trigger is a partial event object (e.g. `{ action: 'click', source:
 * '/map/*' }`) and `compareObjects(event, trigger)` does a full recursive
 * subset compare to decide whether an event fires it. Every subscriber runs
 * this for every trigger on every event, so the common case — the event's
 * `action` simply not matching the trigger's `action` — should be rejected with
 * a single cheap (cached) string compare before recursing into the rest of the
 * payload (source paths, data subtrees).
 */

/**
 * Cheap pre-filter: when both event and trigger declare an `action`, the event
 * can only fire the trigger if the actions match (glob-aware, cached). Returns
 * `false` only when we can prove a mismatch from `action` alone; otherwise
 * `true` (caller must still run the full compare).
 */
const actionCanMatch = (event: any, trigger: any): boolean => {
  if (
    !event ||
    !trigger ||
    typeof event !== 'object' ||
    typeof trigger !== 'object'
  ) {
    return true;
  }
  const triggerAction = trigger.action;
  // Trigger doesn't constrain action → no short-circuit possible.
  if (typeof triggerAction !== 'string') return true;
  const eventAction = event.action;
  // Event has no string action but the trigger requires one → cannot match.
  if (typeof eventAction !== 'string') return false;
  return matchPattern(eventAction, triggerAction);
};

/**
 * Does `event` satisfy `trigger`? Short-circuits on `action` before the full
 * recursive `compareObjects` subset compare.
 */
export const matchesTrigger = (event: any, trigger: any): boolean => {
  if (!actionCanMatch(event, trigger)) return false;
  return compareObjects(event, trigger);
};

/** Does `event` satisfy any of `triggers`? */
export const matchesAnyTrigger = (event: any, triggers: any): boolean => {
  if (!event || !triggers) return false;
  if (!Array.isArray(triggers) || triggers.length === 0) return false;
  return triggers.some((trigger) => matchesTrigger(event, trigger));
};
